import { GRID_SIZE, type NodeId } from '@ipfabric/utils';

import {
  type NodeMovePosition,
  setNodePositions,
} from 'components/Graph/commands/setNodePositions';
import type { GraphCommandFactory } from 'components/Graph/model/GraphCommandContext';
import { getSelectedNodes } from 'components/Graph/model/GraphData';

type Axis = 'x' | 'y';

export interface DistributeSelectedNodesCommand {
  axis: Axis;
}

export const DISTRIBUTION_GAP = GRID_SIZE * 6;

export const distributeSelectedNodes: GraphCommandFactory<
  DistributeSelectedNodesCommand
> = (cmdContext, dispatch) => (command) => {
  const { graph } = cmdContext.getState();
  const selectedNodes = getSelectedNodes(graph);
  if (selectedNodes.length === 0) {
    return;
  }
  const distributionAxis = command.axis;
  const stableAxis = distributionAxis === 'x' ? 'y' : 'x';
  const nodesByStableAxis = selectedNodes.reduce((result, node) => {
    const stableAxisCoordinate = node.position[stableAxis];
    if (!result.has(stableAxisCoordinate)) {
      result.set(stableAxisCoordinate, []);
    }
    result.get(stableAxisCoordinate)!.push(node.id);
    return result;
  }, new Map<number, NodeId[]>());
  const positions: NodeMovePosition[] = [];
  // biome-ignore lint/complexity/noForEach: iteration over Map
  nodesByStableAxis.forEach((nodeIds) => {
    if (nodeIds.length > 1) {
      nodeIds.sort(
        (a, b) =>
          // Fixed noUncheckedIndexedAccess NIM-19408
          graph.nodes[a]!.position[distributionAxis] -
          graph.nodes[b]!.position[distributionAxis],
      );
      // Fixed noUncheckedIndexedAccess NIM-19408
      let lastPosition = graph.nodes[nodeIds[0]!]!.position[distributionAxis];
      // Include first node so setNodePositions can properly handle case when distributing hosts
      positions.push({
        // Fixed noUncheckedIndexedAccess NIM-19408
        nodeId: nodeIds[0]!,
        moveTo: {
          [distributionAxis]: lastPosition,
        },
      });

      for (const nodeId of nodeIds.slice(1)) {
        lastPosition += DISTRIBUTION_GAP;
        positions.push({
          nodeId,
          moveTo: {
            [distributionAxis]: lastPosition,
          },
        });
      }
    }
  });
  if (positions.length > 0) {
    dispatch(setNodePositions, { positions });
  } else {
    cmdContext.showNotification({
      type: 'info',
      message:
        cmdContext.msg.canvasGraph.commands.distribute.noAlignedNodes[
          distributionAxis
        ],
    });
  }
};
